import { collection, query, where, getDocs, getCountFromServer, orderBy, limit, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserSolicitation, UserSolicitationMetadata, SolicitationFilter } from '../types/solicitation';

interface ExtensionSavedSolicitation {
  id: string;
  noticeId?: string;
  title?: string;
  agency?: string;
  department?: string;
  deadline?: string | number | Date;
  url?: string;
  savedAt?: number;
  naicsCode?: string;
  setAside?: string;
}

export class UserSolicitationsService {
  private static instance: UserSolicitationsService;

  private constructor() {}

  public static getInstance(): UserSolicitationsService {
    if (!UserSolicitationsService.instance) {
      UserSolicitationsService.instance = new UserSolicitationsService();
    }
    return UserSolicitationsService.instance;
  }

  public async getUserSolicitations(
    userId: string,
    filter: SolicitationFilter = 'all',
    userEmail?: string
  ): Promise<UserSolicitation[]> {
    try {
      const [websiteSolicitations, extensionSolicitations] = await Promise.all([
        this.fetchWebsiteSolicitations(userId, filter),
        userEmail ? this.fetchExtensionSolicitations(userEmail) : Promise.resolve([]),
      ]);

      const merged = this.mergeSolicitations(websiteSolicitations, extensionSolicitations, userId);

      if (filter !== 'all') {
        return merged.filter(s => s.metadata.status === filter);
      }

      return merged.sort((a, b) => {
        const deadlineA = a.metadata.deadline?.getTime() || 0;
        const deadlineB = b.metadata.deadline?.getTime() || 0;
        return deadlineA - deadlineB;
      });
    } catch (error) {
      console.error('Error fetching user solicitations:', error);
      throw new Error('Failed to fetch solicitations');
    }
  }

  private async fetchWebsiteSolicitations(userId: string, filter: SolicitationFilter): Promise<UserSolicitation[]> {
    const solicitationsRef = collection(db, 'solicitations');
    let q = query(solicitationsRef, where('userId', '==', userId));

    if (filter !== 'all') {
      q = query(solicitationsRef, where('userId', '==', userId), where('metadata.status', '==', filter));
    }

    const querySnapshot = await getDocs(q);
    const solicitations: UserSolicitation[] = [];

    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      const proposalCount = await this.getSubcollectionCount(docSnap.ref.path, 'proposals');
      const emailCount = await this.getSubcollectionCount(docSnap.ref.path, 'emails');

      solicitations.push({
        id: docSnap.id,
        odp: data.odp,
        userId: data.userId,
        metadata: {
          ...data.metadata,
          deadline: data.metadata.deadline?.toDate ? data.metadata.deadline.toDate() : new Date(data.metadata.deadline),
        } as UserSolicitationMetadata,
        notes: data.notes,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        lastModified: data.lastModified?.toDate ? data.lastModified.toDate() : new Date(data.lastModified),
        proposalCount,
        emailCount,
      });
    }

    return solicitations;
  }

  private async fetchExtensionSolicitations(userEmail: string): Promise<UserSolicitation[]> {
    try {
      const savedRef = collection(db, 'users', userEmail, 'savedSolicitations');
      const q = query(savedRef, orderBy('savedAt', 'desc'), limit(100));
      const querySnapshot = await getDocs(q);
      const solicitations: UserSolicitation[] = [];

      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data() as ExtensionSavedSolicitation;
        const converted = this.convertExtensionToUserSolicitation(data, userEmail);
        if (converted) {
          solicitations.push(converted);
        }
      }

      return solicitations;
    } catch (error) {
      console.error('Error fetching extension solicitations:', error);
      return [];
    }
  }

  private convertExtensionToUserSolicitation(
    data: ExtensionSavedSolicitation,
    userId: string
  ): UserSolicitation | null {
    if (!data.id && !data.noticeId) return null;

    const id = data.id || data.noticeId || '';
    const savedAt = data.savedAt ? new Date(data.savedAt) : new Date();
    let deadline: Date;

    if (data.deadline) {
      if (typeof data.deadline === 'number') {
        deadline = new Date(data.deadline);
      } else if (data.deadline instanceof Date) {
        deadline = data.deadline;
      } else {
        deadline = new Date(data.deadline);
      }
    } else {
      deadline = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    }

    return {
      id: `ext_${id}`,
      odp: id,
      userId,
      metadata: {
        title: data.title || 'Untitled Solicitation',
        noticeId: data.noticeId || id,
        agency: data.agency || 'Unknown Agency',
        department: data.department,
        deadline,
        samGovUrl: data.url || `https://sam.gov/opp/${id}/view`,
        naicsCode: data.naicsCode,
        setAside: data.setAside,
        status: 'active',
      },
      notes: undefined,
      createdAt: savedAt,
      lastModified: savedAt,
      proposalCount: 0,
      emailCount: 0,
    };
  }

  private mergeSolicitations(
    website: UserSolicitation[],
    extension: UserSolicitation[],
    userId: string
  ): UserSolicitation[] {
    const merged = new Map<string, UserSolicitation>();

    for (const sol of website) {
      merged.set(sol.metadata.noticeId || sol.id, sol);
    }

    for (const sol of extension) {
      const key = sol.metadata.noticeId || sol.odp;
      if (!merged.has(key)) {
        merged.set(key, sol);
      }
    }

    return Array.from(merged.values());
  }

  private async getSubcollectionCount(parentPath: string, subcollection: string): Promise<number> {
    try {
      const subcollectionRef = collection(db, parentPath, subcollection);
      const snapshot = await getCountFromServer(subcollectionRef);
      return snapshot.data().count;
    } catch (error) {
      return 0;
    }
  }

  public async getSolicitationStats(userId: string, userEmail?: string): Promise<{
    active: number;
    dueSoon: number;
    submitted: number;
  }> {
    try {
      const solicitations = await this.getUserSolicitations(userId, 'all', userEmail);
      const now = new Date();
      const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);

      return {
        active: solicitations.filter(s => s.metadata.status === 'active').length,
        dueSoon: solicitations.filter(s => {
          const deadline = s.metadata.deadline;
          return s.metadata.status === 'active' && deadline && deadline <= threeDaysFromNow && deadline > now;
        }).length,
        submitted: solicitations.filter(s => s.metadata.status === 'submitted').length,
      };
    } catch (error) {
      console.error('Error fetching solicitation stats:', error);
      return { active: 0, dueSoon: 0, submitted: 0 };
    }
  }
}

export const userSolicitationsService = UserSolicitationsService.getInstance();
